// CTAP2 Cryptographic Operations
// Implements PIN protocol and credential management crypto

import {encodeCBOR, parseCBOR, hexStringToByte, byteToHexString} from './util';
import * as CryptoJS from 'crypto-js';

// ==================== ECDH Key Agreement ====================

/**
 * Generate P-256 ECDH key pair
 */
export async function generateECDHKeyPair() {
  return await crypto.subtle.generateKey(
    {
      name: 'ECDH',
      namedCurve: 'P-256'
    },
    true,
    ['deriveKey', 'deriveBits']
  );
}

/**
 * Export public key to COSE format
 */
export async function exportPublicKeyCOSE(publicKey) {
  const jwk = await crypto.subtle.exportKey('jwk', publicKey);
  
  // Convert JWK to COSE_Key format
  // COSE_Key: {1: 2, 3: -25, -1: 1, -2: x, -3: y}
  const x = base64urlToBytes(jwk.x);
  const y = base64urlToBytes(jwk.y);
  
  return {
    1: 2,     // kty: EC2
    3: -25,   // alg: ECDH-ES+HKDF-256
    '-1': 1,  // crv: P-256
    '-2': x,  // x coordinate
    '-3': y   // y coordinate
  };
}

/**
 * Import authenticator's public key from COSE format
 */
export async function importAuthenticatorPublicKey(coseKey) {
  // COSE_Key to JWK
  // JavaScript objects use string keys, so negative integers become strings
  // Try both numeric and string forms
  const xCoord = coseKey['-2'] || coseKey[-2];
  const yCoord = coseKey['-3'] || coseKey[-3];
  
  if (!xCoord || !yCoord) {
    console.error('COSE key structure:', coseKey);
    console.error('Available keys:', Object.keys(coseKey));
    throw new Error('Invalid COSE key: missing x or y coordinates');
  }
  
  const x = base64urlEncode(xCoord);
  const y = base64urlEncode(yCoord);
  
  const jwk = {
    kty: 'EC',
    crv: 'P-256',
    x: x,
    y: y,
    ext: true
  };
  
  return await crypto.subtle.importKey(
    'jwk',
    jwk,
    {
      name: 'ECDH',
      namedCurve: 'P-256'
    },
    true,
    []
  );
}

/**
 * Derive shared secret using ECDH + SHA-256 (CTAP2 PIN Protocol 1)
 * 
 * Protocol 1 flow:
 * 1. ECDH(platform.private, authenticator.public) → rawSecret (32 bytes)
 * 2. SHA-256(rawSecret) → sharedSecret (32 bytes)
 * 
 * The shared secret is then used as:
 * - First 32 bytes: AES-256 encryption key
 * - First 32 bytes: HMAC-SHA256 key (for pinUvAuthParam)
 */
export async function deriveSharedSecret(privateKey, publicKey) {
  // Step 1: ECDH key agreement
  const rawSecret = await crypto.subtle.deriveBits(
    {
      name: 'ECDH',
      public: publicKey
    },
    privateKey,
    256
  );
  
  // Step 2: SHA-256 for PIN Protocol 1
  const sharedSecret = await sha256(new Uint8Array(rawSecret));
  
  return sharedSecret;
}

// ==================== AES-256-CBC Encryption ====================

/**
 * Encrypt data using AES-256-CBC with zero IV (CTAP2 PIN Protocol 1)
 * Protocol 1 uses HMAC-SHA256 and zero IV for AES-CBC
 * 
 * IMPORTANT: Web Crypto API's AES-CBC always adds PKCS#7 padding, even for data
 * that's already a multiple of block size. For 64-byte input, it outputs 80 bytes
 * (64 + 16 padding block). We need to remove this padding to get exactly 64 bytes.
 * 
 * @param {Uint8Array} key - Shared secret (32 bytes for Protocol 1)
 * @param {Uint8Array} data - Data to encrypt (must be multiple of 16 bytes)
 * @returns {Uint8Array} Ciphertext only (same length as input, PKCS#7 padding removed)
 */
export async function encryptAES256CBC(key, data) {
  // Protocol 1: Use zero IV
  const iv = new Uint8Array(16); // All zeros
  
  // Import key (use first 32 bytes only for Protocol 1)
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key.slice(0, 32),
    'AES-CBC',
    false,
    ['encrypt']
  );
  
  // Encrypt (Web Crypto will add PKCS#7 padding)
  const encrypted = await crypto.subtle.encrypt(
    {
      name: 'AES-CBC',
      iv: iv
    },
    cryptoKey,
    data
  );
  
  // Remove PKCS#7 padding block (last 16 bytes)
  // Web Crypto adds a full padding block even when input is already block-aligned
  const result = new Uint8Array(encrypted);
  return result.slice(0, data.length);
}

/**
 * Decrypt data using AES-256-CBC with zero IV (CTAP2 PIN Protocol 1)
 * Protocol 1 uses HMAC-SHA256 and zero IV for AES-CBC
 * 
 * IMPORTANT: CanoKey firmware doesn't use PKCS#7 padding, but Web Crypto API
 * expects it. We need to add dummy padding before decryption and remove it after.
 * 
 * @param {Uint8Array} key - Shared secret (32 bytes for Protocol 1)
 * @param {Uint8Array} data - Ciphertext to decrypt (no IV prepended, no PKCS#7 padding)
 * @returns {Uint8Array} Decrypted plaintext
 */
export async function decryptAES256CBC(key, data) {
  // CanoKey uses raw AES-256-CBC: zero IV, no PKCS#7 padding, 32-byte key
  // Input: 32 bytes ciphertext → Output: 32 bytes plaintext
  
  // Web Crypto API doesn't support no-padding mode, so we use crypto-js
  
  // Convert Uint8Array to WordArray (crypto-js format)
  const keyWordArray = CryptoJS.lib.WordArray.create(key.slice(0, 32));
  const dataWordArray = CryptoJS.lib.WordArray.create(data);
  const ivWordArray = CryptoJS.lib.WordArray.create(new Uint8Array(16)); // Zero IV
  
  // Decrypt using AES-CBC with NoPadding
  const decrypted = CryptoJS.AES.decrypt(
    {ciphertext: dataWordArray},
    keyWordArray,
    {
      iv: ivWordArray,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.NoPadding
    }
  );
  
  // Convert WordArray back to Uint8Array
  const words = decrypted.words;
  const sigBytes = decrypted.sigBytes;
  const result = new Uint8Array(sigBytes);
  
  for (let i = 0; i < sigBytes; i++) {
    result[i] = (words[i >>> 2] >>> (24 - (i % 4) * 8)) & 0xff;
  }
  
  console.log('decryptAES256CBC: Decrypted', result.length, 'bytes');
  return result;
}

// ==================== HMAC-SHA256 ====================

/**
 * Calculate HMAC-SHA256
 */
export async function hmacSHA256(key, data) {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key,
    {
      name: 'HMAC',
      hash: 'SHA-256'
    },
    false,
    ['sign']
  );
  
  const signature = await crypto.subtle.sign(
    'HMAC',
    cryptoKey,
    data
  );
  
  return new Uint8Array(signature);
}

/**
 * Calculate pinUvAuthParam (first 16 bytes of HMAC-SHA256)
 */
export async function calculatePinUvAuthParam(pinToken, data) {
  const hmac = await hmacSHA256(pinToken, data);
  return hmac.slice(0, 16);
}

// ==================== SHA-256 ====================

/**
 * Calculate SHA-256 hash
 */
export async function sha256(data) {
  const hash = await crypto.subtle.digest('SHA-256', data);
  return new Uint8Array(hash);
}

/**
 * Calculate SHA-256 hash of a PIN (for PIN protocol)
 */
export async function hashPin(pin) {
  const pinBytes = new TextEncoder().encode(pin);
  return await sha256(pinBytes);
}

// ==================== PIN Protocol Helpers ====================

/**
 * Pad PIN to 64 bytes
 */
export function padPin(pin) {
  const pinBytes = new TextEncoder().encode(pin);
  const padded = new Uint8Array(64);
  padded.set(pinBytes, 0);
  return padded;
}

/**
 * Left-pad PIN hash to 16 bytes (for older authenticators)
 */
export function leftPadPinHash(pinHash) {
  if (pinHash.length === 16) return pinHash;
  const padded = new Uint8Array(16);
  padded.set(pinHash.slice(0, 16), 0);
  return padded;
}

// ==================== Base64URL Helpers ====================

function base64urlToBytes(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const padLen = (4 - (base64.length % 4)) % 4;
  const padded = base64 + '='.repeat(padLen);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function base64urlEncode(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

// ==================== CBOR Encoding for CTAP2 ====================

/**
 * Encode CBOR data and return as hex string
 */
export function encodeCBORHex(data) {
  const cbor = encodeCBOR(data);
  return byteToHexString(cbor);
}

/**
 * Parse CBOR from hex string
 */
export function parseCBORHex(hex) {
  const bytes = hexStringToByte(hex);
  return parseCBOR(bytes);
}

